import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RACHEL_VOICES, SARA_VOICES, SELENE_VOICES, ADAM_VOICES } from '../ea/characterVoices';
import GeneratedImage from './GeneratedImage';
import { useGeneratedImages } from '../hooks/useGeneratedImages';

// Per-character voice lists sourced directly from the character modules.
// Used to filter voice dropdowns so each character only shows their own voices.
const CHARACTER_VOICES = {
  SELENE: SELENE_VOICES,
  RACHEL: RACHEL_VOICES,
  SARA:   SARA_VOICES,
  ADAM:   ADAM_VOICES,
};

// Back room cloud functions are intentionally disabled (Copilot cost-control move
// — back-room chat is chatty and was the biggest cost lever). All back-room actions
// must route through the localhost server (start-backroom-localhost.ps1, port 8787).
const BACKROOM_LOCAL_BASE = 'http://127.0.0.1:8787';

// Hard timeouts so a slow/hung localhost server can never freeze the UI.
// Chat goes through OpenAI so 60s is realistic; config/debug are local file
// writes so 8s is plenty. AbortController throws a clean error on expiry.
async function backroomLocalFetch(path, init, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const r = await fetch(`${BACKROOM_LOCAL_BASE}${path}`, { ...init, signal: controller.signal });
    if (!r.ok) {
      const detail = await r.text().catch(() => '');
      throw new Error(`Localhost ${path} ${r.status}: ${detail.slice(0, 200) || 'request failed'}`);
    }
    const data = await r.json();
    return { data };
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error(`Localhost ${path} timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function backroomLocalPost(path, body) {
  const timeoutMs = path === '/backroom/chat' ? 60000 : 8000;
  const init = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  };
  try {
    return await backroomLocalFetch(path, init, timeoutMs);
  } catch (err) {
    const msg = String(err?.message || '').toLowerCase();
    const retryable = msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('network error');
    if (!retryable) throw err;
    // One quick retry for transient localhost restart/network blips.
    return backroomLocalFetch(path, init, timeoutMs);
  }
}

async function backroomLocalGet(path) {
  return backroomLocalFetch(path, { method: 'GET' }, 8000);
}

const SELENE_IDLE_VIDEO = '/selene-idle.mp4';
const SELENE_TALKING_VIDEO = '/selene-talking.mp4';
const SELENE_POSTER = '/icons/icon-192.png';
const BR_SESSION_KEY = 'backroom_session_v1';
const BR_CHATS_KEY = 'backroom_chats_v1';
const BR_ACTIVE_CHAT_KEY = 'backroom_active_chat_v1';
const BR_AUTO_GROUP_KEY = 'brAutoGroup';
const BR_AUTO_GROUP_MAX_KEY = 'brAutoGroupMaxTurns';
const BR_AUTO_GROUP_DELAY_MS = 350;
const BR_IMG_MAX_PX = 1024;   // longest edge after resize
const BR_IMG_QUALITY = 0.82;  // JPEG quality — keeps token cost reasonable
const BR_IMG_MAX_BYTES = 10 * 1024 * 1024; // 10 MB hard limit before resize
const BOT_RESPONSE_DELAY_MS = 650;
const BACKROOM_THEME_FILTERS = {
  auto: 'none',
  ember: 'none',
  ocean: 'hue-rotate(155deg) saturate(1.08)',
  forest: 'hue-rotate(75deg) saturate(1.06)',
  plum: 'hue-rotate(215deg) saturate(1.1)',
};
// ============================================================
// BackRoom.jsx
// Private owner-only AI companion — session-only, nothing persisted.
// Slide-in panel from the right. Visually distinct from EA.
// Auth enforced server-side (UID check in backRoomChat action).
// ============================================================

function renderSegmentParts(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[(?:whisper|sigh|laugh)\][\s\S]*?\[\/(?:whisper|sigh|laugh)\]|!\[[^\]]*\]\([^\)]+\)|\[video:[^\]]*\]\([^\)]+\)|\[[^\]]+\]\([^\)]+\))/gi);
  return parts.map((part, i) => {
    const imageMatch = part.match(/^!\[([^\]]*)\]\(([^\)]+)\)$/);
    if (imageMatch) {
      const alt = imageMatch[1] || 'image';
      const src = imageMatch[2] || '';
      return (
        <img
          key={i}
          src={src}
          alt={alt}
          loading="lazy"
          style={{ display: 'block', maxWidth: '100%', maxHeight: '320px', borderRadius: '8px', border: '1px solid rgba(196,125,46,0.2)', margin: '6px 0' }}
        />
      );
    }
    const videoMatch = part.match(/^\[video:([^\]]*)\]\(([^\)]+)\)$/i);
    if (videoMatch) {
      const label = videoMatch[1] || 'video';
      const src = videoMatch[2] || '';
      return (
        <div key={i} style={{ margin: '6px 0' }}>
          <video controls preload="metadata" style={{ display: 'block', maxWidth: '100%', maxHeight: '320px', borderRadius: '8px', border: '1px solid rgba(196,125,46,0.2)' }}>
            <source src={src} />
          </video>
          <div style={{ fontSize: '10px', color: '#8a6a48', marginTop: '3px' }}>{label}</div>
        </div>
      );
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^\)]+)\)$/);
    if (linkMatch) {
      const label = linkMatch[1] || 'link';
      const href = linkMatch[2] || '';
      return (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#d4956a', textDecoration: 'underline' }}>
          {label}
        </a>
      );
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} style={{ color: '#d4956a' }}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <span key={i} style={{ color: '#b6aac2' }}>{part.slice(1, -1)}</span>;
    }
    const whisperMatch = part.match(/^\[whisper\]([\s\S]*?)\[\/whisper\]$/i);
    if (whisperMatch) {
      return <span key={i} style={{ opacity: 0.65, fontStyle: 'italic', letterSpacing: '0.02em' }}>{whisperMatch[1]}</span>;
    }
    const sighMatch = part.match(/^\[sigh\]([\s\S]*?)\[\/sigh\]$/i);
    if (sighMatch) {
      return <span key={i} style={{ color: '#a090b0', fontStyle: 'italic' }}>{sighMatch[1]}</span>;
    }
    const laughMatch = part.match(/^\[laugh\]([\s\S]*?)\[\/laugh\]$/i);
    if (laughMatch) {
      return <span key={i} style={{ color: '#d4956a' }}>{laughMatch[1]}</span>;
    }
    return <span key={i}>{part}</span>;
  });
}

function renderMarkdown(text) {
  if (!text) return null;
  const charSegments = parseCharacterSegments(text);
  const isMultiChar = charSegments.length > 1 || charSegments[0]?.speaker !== 'SELENE';
  if (!isMultiChar) return renderSegmentParts(text);
  return charSegments.map((seg, i) => {
    const voice = DEFAULT_VOICE_MAP[seg.speaker] || DEFAULT_VOICE_MAP.SELENE;
    return (
      <div key={i} style={{ marginBottom: i < charSegments.length - 1 ? '10px' : 0 }}>
        <span style={{
          fontSize: '10px', color: voice.color, fontWeight: 700,
          letterSpacing: '0.6px', textTransform: 'uppercase',
          display: 'block', marginBottom: '3px', opacity: 0.85,
        }}>
          {voice.label}
        </span>
        {renderSegmentParts(seg.text)}
      </div>
    );
  });
}

function makeChatId() {
  return `br_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function delayBotResponse(ms = BOT_RESPONSE_DELAY_MS) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function summarizeChat(messages) {
  const firstUser = (messages || []).find((m) => m?.role === 'user' && typeof m?.content === 'string' && m.content.trim());
  if (firstUser) return firstUser.content.trim().slice(0, 48);
  return 'New Back Room Chat';
}

// Emotion tags Selene can use. Whisper gets audio processing; others are
// converted to inline ElevenLabs sound markers before TTS dispatch.
const EMOTION_TAGS = ['whisper', 'sigh', 'laugh'];

// Standalone ElevenLabs sound-act markers that survive cleanTtsText and reach
// the Firebase function. Must stay in sync with ELEVENLABS_SPEECH_ACTS in
// functions/index.js.
const TTS_PASSTHROUGH_TAGS = new Set([
  'laughing', 'whispering', 'sighing', 'crying', 'screaming',
  'gasping', 'coughing', 'sniffling', 'groaning', 'yawning',
  'singing', 'whimpering', 'giggling', 'sobbing', 'chuckling', 'moaning',
]);

// Character identity (label, color) is fixed; voice (id, engine) is picker-driven.
// Catalog of available voices is fetched from the server's getAppConfig at runtime
// — these defaults are only used until that call returns.
const DEFAULT_VOICE_MAP = {
  SELENE: { id: '56bWURjYFHyYyVf490Dp', engine: 'elevenlabs', label: 'Selene', color: '#c47d2e' },
  ADAM:   { id: 'UgBBYS2sOqTuMpoF3BR0', engine: 'elevenlabs', label: 'Adam',   color: '#6a9abf' },
  RACHEL: { id: 'ztqW7U07ITK9TRp5iDUi', engine: 'elevenlabs', label: 'Rachel', color: '#7abf8a' },
  SARA:   { id: 'BqV7FvOFFhPNhRcqujKF', engine: 'elevenlabs', label: 'Sara',   color: '#9aa6d9' },
};

// Fallback catalog used until the server returns the real one. Server is the
// source of truth — see local-runner/server/shared.js voice catalogs.
const FALLBACK_OPENAI_VOICE_IDS = new Set(['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer']);

const FALLBACK_VOICE_CATALOG = {
  elevenlabs: [
    ...RACHEL_VOICES,
    ...SARA_VOICES,
    ...SELENE_VOICES,
    ...ADAM_VOICES,
  ],
  openai: [
    { id: 'alloy',   label: 'OpenAI: alloy' },
    { id: 'echo',    label: 'OpenAI: echo' },
    { id: 'fable',   label: 'OpenAI: fable' },
    { id: 'onyx',    label: 'OpenAI: onyx' },
    { id: 'nova',    label: 'OpenAI: nova' },
    { id: 'shimmer', label: 'OpenAI: shimmer' },
  ],
};

function inferVoiceEngine(id) {
  if (!id) return 'elevenlabs';
  if (FALLBACK_OPENAI_VOICE_IDS.has(id)) return 'openai';
  return 'elevenlabs';
}

// Accepts the saved voice config in either shape:
//   legacy: 'voiceIdString'
//   new:    { id, engine }
// Returns a clean { id, engine } or null when no valid id present.
function normalizeVoiceOverride(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    const cleaned = raw.trim();
    if (!cleaned) return null;
    const colonIdx = cleaned.indexOf(':');
    if (colonIdx > 0) {
      const engine = cleaned.slice(0, colonIdx).trim();
      const id = cleaned.slice(colonIdx + 1).trim();
      if (!id) return null;
      if (engine === 'openai' || engine === 'elevenlabs') {
        return { id, engine };
      }
    }
    const id = cleaned;
    if (!id) return null;
    return { id, engine: inferVoiceEngine(id) };
  }
  if (typeof raw === 'object') {
    const id = typeof raw.id === 'string' ? raw.id.trim() : '';
    if (!id) return null;
    const engine = raw.engine === 'openai' || raw.engine === 'elevenlabs'
      ? raw.engine
      : inferVoiceEngine(id);
    return { id, engine };
  }
  return null;
}

function mergeVoiceMap(overrides = {}) {
  const merged = { ...DEFAULT_VOICE_MAP };
  for (const key of Object.keys(merged)) {
    const cleaned = normalizeVoiceOverride(overrides?.[key]);
    if (cleaned) merged[key] = { ...merged[key], id: cleaned.id, engine: cleaned.engine };
  }
  return merged;
}

// Strip UI-only fields (label, color) when sending the voice map back to the server.
function serializeVoiceMap(map) {
  return Object.fromEntries(
    Object.entries(map).map(([k, v]) => [k, { id: v.id, engine: v.engine }])
  );
}

function parseCharacterSegments(text) {
  if (!text) return [{ speaker: 'SELENE', text: '' }];
  const parts = text.split(/\[(SELENE|ADAM|RACHEL|SARA)\]\s*/gi);
  if (parts.length === 1) return [{ speaker: 'SELENE', text }];
  const segments = [];
  if (parts[0].trim()) segments.push({ speaker: 'SELENE', text: parts[0].trim() });
  for (let i = 1; i < parts.length - 1; i += 2) {
    const speaker = parts[i].toUpperCase();
    const content = (parts[i + 1] || '').trim();
    if (content) segments.push({ speaker, text: content });
  }
  return segments.length ? segments : [{ speaker: 'SELENE', text }];
}

function parseSeleneSegments(text) {
  const tagPattern = new RegExp(
    `\\[(${EMOTION_TAGS.join('|')})\\]([\\s\\S]*?)\\[\\/(${EMOTION_TAGS.join('|')})\\]`,
    'gi'
  );
  const segments = [];
  let lastIndex = 0;
  let match;
  while ((match = tagPattern.exec(text)) !== null) {
    const tag = match[1].toLowerCase();
    const content = match[2].trim();
    if (match.index > lastIndex) {
      const normal = cleanTtsText(text.slice(lastIndex, match.index));
      if (normal) segments.push({ text: normal, whisper: false });
    }
    if (content) {
      const elSound = { sigh: '[sighing]', laugh: '[laughing]' }[tag];
      segments.push({ text: elSound ? `${elSound} ${content}` : content, whisper: tag === 'whisper' });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    const tail = cleanTtsText(text.slice(lastIndex));
    if (tail) segments.push({ text: tail, whisper: false });
  }
  return segments.length ? segments : [{ text: cleanTtsText(text), whisper: false }];
}

function cleanTtsText(text) {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/\[([^\]]+)\]/g, (match, inner) =>
      TTS_PASSTHROUGH_TAGS.has(inner.toLowerCase().trim()) ? match : ''
    )
    .replace(/\s+/g, ' ')
    .trim();
}

function sanitizeForModel(text) {
  return String(text || '')
    .replace(/!\[([^\]]*)\]\(data:[^\)]+\)/gi, (_, alt) => `[Image attached: ${alt || 'image'}]`)
    .replace(/\[video:([^\]]*)\]\(data:[^\)]+\)/gi, (_, label) => `[Video attached: ${label || 'video'}]`)
    .replace(/\[[^\]]+\]\(data:[^\)]+\)/gi, '[Attachment link]');
}

// Resize an image File/Blob to max BR_IMG_MAX_PX on the longest edge,
// returns a JPEG data URL. Stays on the client — nothing uploaded.
function resizeImageForVision(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) { reject(new Error('Not an image')); return; }
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > BR_IMG_MAX_PX || height > BR_IMG_MAX_PX) {
        const scale = BR_IMG_MAX_PX / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', BR_IMG_QUALITY));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

function hasMultipleTaggedSpeakers(text) {
  const matches = String(text || '').match(/\[(SELENE|ADAM|RACHEL|SARA)\]/gi) || [];
  const speakers = new Set(matches.map((m) => m.replace(/[\[\]]/g, '').toUpperCase()));
  return speakers.size >= 2;
}

async function playSegment(audioBase64, mimeType, whisper, rate = 1) {
  const dataUrl = `data:${mimeType};base64,${audioBase64}`;
  if (!whisper) {
    const audio = new Audio(dataUrl);
    audio.playbackRate = Math.min(2, Math.max(0.5, rate));
    return new Promise((resolve) => {
      audio.onended = resolve;
      audio.onerror = resolve;
      audio.play().catch(resolve);
    });
  }
  // Whisper: use Web Audio API — cut bass, drop gain
  const arrayBuffer = await fetch(dataUrl).then((r) => r.arrayBuffer());
  const ctx = new AudioContext();
  const decoded = await ctx.decodeAudioData(arrayBuffer);
  const source = ctx.createBufferSource();
  source.buffer = decoded;
  const highpass = ctx.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 180;
  const gain = ctx.createGain();
  gain.gain.value = 0.55;
  source.connect(highpass);
  highpass.connect(gain);
  gain.connect(ctx.destination);
  return new Promise((resolve) => {
    source.onended = () => { ctx.close(); resolve(); };
    source.start();
  });
}

export default function BackRoom({ onClose, fullPage = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [pendingImages, setPendingImages] = useState([]); // [{ dataUrl, name }]
  const [loading, setLoading] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(() =>
    window.localStorage.getItem('brTtsEnabled') !== 'false'
  );
  const [activeVoices, setActiveVoices] = useState(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem('brActiveVoices') || '{}');
      const next = {
        SELENE: !!saved.SELENE,
        ADAM: !!saved.ADAM,
        RACHEL: !!saved.RACHEL,
        SARA: !!saved.SARA,
      };
      if (!next.SELENE && !next.ADAM && !next.RACHEL && !next.SARA) next.SELENE = true;
      return next;
    }
    catch {
      return { SELENE: true, ADAM: true, RACHEL: true, SARA: false };
    }
  });
  const [autoGroupEnabled, setAutoGroupEnabled] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(BR_AUTO_GROUP_KEY) !== 'false';
  });
  const [autoGroupMaxTurns, setAutoGroupMaxTurns] = useState(() => {
    if (typeof window === 'undefined') return 3;
    const raw = Number(window.localStorage.getItem(BR_AUTO_GROUP_MAX_KEY));
    if (!Number.isFinite(raw)) return 3;
    return Math.min(6, Math.max(1, Math.floor(raw)));
  });
  const [voiceMap, setVoiceMap] = useState(() => {
    try {
      return mergeVoiceMap(JSON.parse(window.localStorage.getItem('brVoiceMap') || '{}'));
    } catch {
      return DEFAULT_VOICE_MAP;
    }
  });
  const [voiceCatalog, setVoiceCatalog] = useState(FALLBACK_VOICE_CATALOG);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [ttsError, setTtsError] = useState('');
  const [speechRate, setSpeechRate] = useState(() => {
    const saved = Number(window.localStorage.getItem('brSpeechRate'));
    return Number.isFinite(saved) && saved >= 0.5 && saved <= 2 ? saved : 1;
  });
  const [turnModeLight, setTurnModeLight] = useState('unknown');
  const [startupChecks, setStartupChecks] = useState([]);
  const [startupVerified, setStartupVerified] = useState(null);
  const [showStartupChecks, setShowStartupChecks] = useState(true);
  const [refreshingStartupChecks, setRefreshingStartupChecks] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminSaveMsg, setAdminSaveMsg] = useState('');
  const [lastRecap, setLastRecap] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem('brLastRecap') || 'null'); } catch { return null; }
  });
  const [chatDragActive, setChatDragActive] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [chatRows, setChatRows] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [backroomTheme, setBackroomTheme] = useState(() => {
    if (typeof window === 'undefined') return 'auto';
    const saved = window.localStorage.getItem('brTheme');
    return saved && BACKROOM_THEME_FILTERS[saved] ? saved : 'auto';
  });

  // Generated images management
  const { storeImage, getImage, clearImages } = useGeneratedImages();

  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const lastSpokenRef = useRef('');
  const mountedRef = useRef(true);
  const suppressAutoSpeakRef = useRef(false);
  const cancelRef = useRef(false);
  const speechRateRef = useRef(1);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  // Local VOSK STT state (local-only, sends audio to a running VOSK HTTP service)
  const [localSttSupported, setLocalSttSupported] = useState(() => !!(navigator.mediaDevices && window.MediaRecorder && window.AudioContext));
  const [sttRecording, setSttRecording] = useState(false);
  const sttRecorderRef = useRef(null);
  const sttStreamRef = useRef(null);
  const sttChunksRef = useRef([]);
  const [sttStatus, setSttStatus] = useState('');

  async function startLocalStt() {
    if (!localSttSupported) {
      setSttStatus('Local STT unsupported in this browser. Use Chrome/Edge.');
      return;
    }
    try {
      setSttStatus('Requesting microphone…');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      sttStreamRef.current = stream;
      sttChunksRef.current = [];
      const options = { mimeType: 'audio/webm;codecs=opus' };
      const recorder = new MediaRecorder(stream, options);
      recorder.ondataavailable = (e) => { if (e.data && e.data.size) sttChunksRef.current.push(e.data); };
      recorder.onstart = () => { setSttRecording(true); setSttStatus('Recording…'); };
      recorder.onstop = async () => {
        setSttStatus('Processing audio…');
        try {
          const blob = new Blob(sttChunksRef.current, { type: 'audio/webm' });
          const wavBlob = await blobToWav16(blob);
          await sendRecordedToLocalVosk(wavBlob);
        } catch (err) {
          setSttStatus('STT failed: ' + (err?.message || String(err)));
        } finally {
          setSttRecording(false);
          try { sttStreamRef.current?.getTracks()?.forEach(t => t.stop()); } catch {}
          sttStreamRef.current = null;
          sttChunksRef.current = [];
        }
      };
      recorder.start();
      sttRecorderRef.current = recorder;
    } catch (err) {
      setSttStatus('Microphone access denied or unavailable.');
      sttRecorderRef.current = null; sttStreamRef.current = null; sttChunksRef.current = [];
    }
  }

  function stopLocalStt() {
    try {
      const recorder = sttRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
    } catch (err) {
      setSttStatus('Failed to stop recording.');
    }
  }

  async function sendRecordedToLocalVosk(wavBlob) {
    // Endpoint expected: http://127.0.0.1:2700/transcribe or user-provided
    const url = window.localStorage.getItem('brLocalSttUrl') || 'http://127.0.0.1:2700/transcribe';
    try {
      const fd = new FormData();
      fd.append('audio', wavBlob, 'recording.wav');
      const res = await fetch(url, { method: 'POST', body: fd });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Local STT error ${res.status}: ${txt.slice(0,200)}`);
      }
      const data = await res.json().catch(() => null);
      const text = data?.text || data?.result || data?.transcript || '';
      if (text && text.trim()) {
        setInput((prev) => (prev.trim() ? prev + '\n' + text : text));
        setSttStatus('Transcription ready — edit or send.');
      } else {
        setSttStatus('No speech detected.');
      }
    } catch (err) {
      setSttStatus('STT request failed: ' + (err?.message || String(err)));
    }
  }

  // Convert recorded blob (webm/opus) to 16k WAV Blob suitable for VOSK
  async function blobToWav16(blob) {
    const arrayBuffer = await blob.arrayBuffer();
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
    const channelData = audioBuffer.numberOfChannels > 0 ? audioBuffer.getChannelData(0) : new Float32Array(0);
    const sampleRate = audioBuffer.sampleRate;
    const desiredRate = 16000;
    const down = downsampleBuffer(channelData, sampleRate, desiredRate);
    const wavBuffer = encodeWAV(down, desiredRate);
    return new Blob([wavBuffer], { type: 'audio/wav' });
  }

  function downsampleBuffer(buffer, sampleRate, outSampleRate) {
    if (outSampleRate === sampleRate) return buffer;
    const sampleRateRatio = sampleRate / outSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < newLength) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      let accum = 0, count = 0;
      for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) { accum += buffer[i]; count++; }
      result[offsetResult] = count > 0 ? accum / count : 0;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }
    return result;
  }

  function encodeWAV(samples, sampleRate) {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    /* RIFF identifier */ writeString(view, 0, 'RIFF');
    /* file length */ view.setUint32(4, 36 + samples.length * 2, true);
    /* RIFF type */ writeString(view, 8, 'WAVE');
    /* format chunk identifier */ writeString(view, 12, 'fmt ');
    /* format chunk length */ view.setUint32(16, 16, true);
    /* sample format (raw) */ view.setUint16(20, 1, true);
    /* channel count */ view.setUint16(22, 1, true);
    /* sample rate */ view.setUint32(24, sampleRate, true);
    /* byte rate (sampleRate * blockAlign) */ view.setUint32(28, sampleRate * 2, true);
    /* block align (channel count * bytes per sample) */ view.setUint16(32, 2, true);
    /* bits per sample */ view.setUint16(34, 16, true);
    /* data chunk identifier */ writeString(view, 36, 'data');
    /* data chunk length */ view.setUint32(40, samples.length * 2, true);
    // Write the PCM samples
    let offset = 44;
    for (let i = 0; i < samples.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return view;

    function writeString(view, offset, str) {
      for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
    }
  }
  const isMobile = window.innerWidth < 640;

  const functions = getFunctions();
  // Back room chat/config/debug → localhost (cloud is disabled by design).
  // TTS and app-config → still cloud (small read-only calls; cheap).
  //
  // Circuit breaker: if the localhost server is down or any back-room call
  // throws repeatedly, we stop calling after CIRCUIT_BREAK consecutive
  // failures and surface a single visible error. This prevents the kind of
  // runaway retry loop that hammered the cloud on 2026-05-30.
  const CIRCUIT_BREAK = 3;
  const consecutiveFailuresRef = useRef(0);
  const [backroomDown, setBackroomDown] = useState(false);
  const isRateLimitError = (err) => {
    const msg = String(err?.message || '').toLowerCase();
    return msg.includes('openai error 429')
      || msg.includes('rate limit reached')
      || msg.includes('tokens per min')
      || msg.includes('tpm')
      || msg.includes('too many requests');
  };
  const localCallWithBreaker = useCallback(async (label, runner) => {
    if (consecutiveFailuresRef.current >= CIRCUIT_BREAK) {
      throw new Error(`Back room is offline (${label}) — restart the localhost server and refresh.`);
    }
    try {
      const result = await runner();
      consecutiveFailuresRef.current = 0;
      return result;
    } catch (err) {
      if (isRateLimitError(err)) {
        // 429 is upstream throttling, not localhost outage. Do not trip breaker.
        throw new Error('OpenAI is rate-limited right now. Wait a few seconds and retry.');
      }
      consecutiveFailuresRef.current += 1;
      if (consecutiveFailuresRef.current >= CIRCUIT_BREAK) {
        setBackroomDown(true);
      }
      throw err;
    }
  }, []);
  const backRoomChat = useCallback(({ messages, activeVoices }) =>
    localCallWithBreaker('chat', () => backroomLocalPost('/backroom/chat', { messages, activeVoices })),
  [localCallWithBreaker]);
  const setBackRoomConfigFn = useCallback((payload) =>
    localCallWithBreaker('config', () => backroomLocalPost('/backroom/config', payload)),
  [localCallWithBreaker]);
  const getBackRoomDebug = useCallback(() =>
    localCallWithBreaker('debug', () => backroomLocalGet('/backroom/debug')),
  [localCallWithBreaker]);
  const synthesizeSelenesSpeech = httpsCallable(functions, 'synthesizeSelenesSpeech');
  const synthesizeEaSpeech = httpsCallable(functions, 'synthesizeEaSpeech');
  const getAppConfig = httpsCallable(functions, 'getAppConfig');

  useEffect(() => {
    let cancelled = false;
    getAppConfig({}).then((result) => {
      if (cancelled) return;
      // Only overwrite local voice map if the user has no saved local config.
      // If brVoiceMap exists in localStorage the user has already customized voices
      // — trust that over whatever the cloud function returns.
      const hasLocalVoiceConfig = !!window.localStorage.getItem('brVoiceMap');
      if (!hasLocalVoiceConfig) {
        const nextMap = mergeVoiceMap(result?.data?.backRoomVoices || {});
        setVoiceMap(nextMap);
        window.localStorage.setItem('brVoiceMap', JSON.stringify(serializeVoiceMap(nextMap)));
      }
      const catalog = result?.data?.voiceCatalog;
      if (catalog && (Array.isArray(catalog.elevenlabs) || Array.isArray(catalog.openai))) {
        setVoiceCatalog({
          elevenlabs: Array.isArray(catalog.elevenlabs) ? catalog.elevenlabs : FALLBACK_VOICE_CATALOG.elevenlabs,
          openai: Array.isArray(catalog.openai) ? catalog.openai : FALLBACK_VOICE_CATALOG.openai,
        });
      }
    }).catch(() => {
      // Keep cached defaults if config is unavailable.
    });
    return () => { cancelled = true; };
  }, [getAppConfig]);

  useEffect(() => {
    let cancelled = false;
    getBackRoomDebug({ _action: 'getBackRoomDebug' }).then((result) => {
      if (cancelled) return;
      setDebugData(result?.data || null);
    }).catch((err) => {
      if (cancelled) return;
      setDebugData({ error: err?.message || 'Unable to load Back Room debug data.' });
    });
    return () => { cancelled = true; };
  }, [getBackRoomDebug]);

  // Optimistic save: UPDATE the local React state first so the dropdown
  // reflects the user's choice instantly, THEN persist to the localhost
  // server in the background. If the save fails, revert the state and
  // surface an error. This is what makes the picker feel snappy — no more
  // dropdown-snap-back-to-old-value while waiting on a 60s OpenAI call.
  //
  // Accepts edits in either shape:
  //   legacy: { SELENE: 'voiceIdString' }   ← admin panel text inputs
  //   new:    { SELENE: { id, engine } }    ← sidebar picker
  async function handleSaveVoices(edits) {
    const cleaned = {};
    for (const [k, raw] of Object.entries(edits || {})) {
      const v = normalizeVoiceOverride(raw);
      if (v) cleaned[k] = v;
    }
    if (Object.keys(cleaned).length === 0) { setAdminSaveMsg('No voices to save.'); return; }

    // Snapshot for rollback on failure.
    const previousMap = voiceMap;

    // OPTIMISTIC UPDATE — state changes immediately, dropdown updates immediately.
    setVoiceMap((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(cleaned)) {
        if (next[k]) next[k] = { ...next[k], id: v.id, engine: v.engine };
      }
      window.localStorage.setItem('brVoiceMap', JSON.stringify(serializeVoiceMap(next)));
      return next;
    });
    setAdminSaving(true); setAdminSaveMsg('');

    try {
      await setBackRoomConfigFn({ voiceMap: cleaned });
      setAdminSaveMsg('Voices saved ✓');
    } catch (err) {
      // Revert on failure so the UI doesn't lie about what's saved.
      setVoiceMap(previousMap);
      window.localStorage.setItem('brVoiceMap', JSON.stringify(serializeVoiceMap(previousMap)));
      setAdminSaveMsg('Save failed (reverted): ' + (err?.message || 'Unknown error'));
    } finally {
      setAdminSaving(false);
      setTimeout(() => setAdminSaveMsg(''), 5000);
    }
  }

  // Single-character voice pick — LOCAL ONLY.
  // Updates React state and localStorage immediately with no server round-trip.
  // Call saveVoiceDefaultsToServer() to persist to the server.
  function setCharacterVoice(character, id, engine) {
    setVoiceMap((prev) => {
      const next = { ...prev };
      if (next[character]) next[character] = { ...next[character], id, engine };
      window.localStorage.setItem('brVoiceMap', JSON.stringify(serializeVoiceMap(next)));
      return next;
    });
  }

  // Push the current voiceMap to the localhost server — called only by "Save as Default".
  async function saveVoiceDefaultsToServer() {
    setAdminSaving(true); setAdminSaveMsg('');
    try {
      await setBackRoomConfigFn({ voiceMap });
      setAdminSaveMsg('Voices saved as default ✓');
    } catch (err) {
      setAdminSaveMsg('Save failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setAdminSaving(false);
      setTimeout(() => setAdminSaveMsg(''), 5000);
    }
  }

  // Speak a one-shot phrase as a specific character regardless of whether
  // they are currently toggled on. Used by the sidebar Test button.
  async function testCharacterVoice(character) {
    const v = voiceMap[character];
    if (!v) return;
    const phrase = `Hi Rodger, this is ${v.label}.`;
    setTtsError('');
    setTtsPlaying(true);
    try {
      const useOpenAi = v.engine === 'openai';
      const synth = useOpenAi ? synthesizeEaSpeech : synthesizeSelenesSpeech;
      const payload = useOpenAi
        ? { text: phrase, voice: v.id }
        : { text: phrase, voiceId: v.id };
      const r = await synth(payload);
      const audioBase64 = r?.data?.audioBase64 || '';
      const mimeType = r?.data?.mimeType || 'audio/mpeg';
      if (!audioBase64) {
        setTtsError(`No audio returned for ${character} (${v.engine} ${v.id})`);
        return;
      }
      stopAudio();
      await playSegment(audioBase64, mimeType, false, speechRateRef.current);
    } catch (err) {
      const msg = err?.message || String(err);
      setTtsError(`Test failed (${character}): ${msg}`);
    } finally {
      if (mountedRef.current) setTtsPlaying(false);
    }
  }

  async function handleSaveMemoryField(field, value) {
    setAdminSaving(true); setAdminSaveMsg('');
    try {
      await setBackRoomConfigFn({ _action: 'setBackRoomConfig', memoryField: field, memoryValue: value });
      const result = await getBackRoomDebug({ _action: 'getBackRoomDebug' });
      setDebugData(result?.data || null);
      setAdminSaveMsg(field.replace(/_/g, ' ') + ' saved ✓');
    } catch (err) {
      setAdminSaveMsg('Save failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setAdminSaving(false);
      setTimeout(() => setAdminSaveMsg(''), 5000);
    }
  }

  async function handleChatDrop(event) {
    event.preventDefault();
    setChatDragActive(false);
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length === 0) return;
    const textParts = [];
    for (const file of files.slice(0, 4)) {
      if (isImageFile(file)) {
        if (file.size > BR_IMG_MAX_BYTES) { textParts.push(`🖼️ ${file.name} — too large to attach (>10 MB).`); continue; }
        try {
          const dataUrl = await resizeImageForVision(file);
          setPendingImages((prev) => [...prev, { dataUrl, name: file.name }]);
        } catch {
          textParts.push(`🖼️ ${file.name} — preview failed.`);
        }
      } else if (isVideoFile(file)) {
        try {
          const dataUrl = await readFileAsDataUrl(file);
          textParts.push(`🎬 **${file.name}**\n[video:${file.name}](${dataUrl})`);
        } catch {
          textParts.push(`🎬 ${file.name} — video dropped (preview unavailable).`);
        }
      } else if (isTextFile(file)) {
        try {
          const text = await readFileAsText(file);
          const preview = text.length > 8000 ? text.slice(0, 8000) + '\n[… truncated at 8000 chars]' : text;
          textParts.push(`📄 **${file.name}**\n\`\`\`\n${preview}\n\`\`\``);
        } catch {
          textParts.push(`[Could not read ${file.name}]`);
        }
      } else {
        textParts.push(`📎 ${file.name} (${file.type || 'unknown type'}) — not a readable file type yet.`);
      }
    }
    if (textParts.length > 0) {
      setInput((prev) => (prev.trim() ? prev + '\n\n' : '') + textParts.join('\n\n'));
    }
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  // Restore chat history and active chat.
  useEffect(() => {
    try {
      const rawChats = window.localStorage.getItem(BR_CHATS_KEY);
      let parsedChats = rawChats ? JSON.parse(rawChats) : [];
      if (!Array.isArray(parsedChats)) parsedChats = [];

      // Migrate legacy single-session cache if present.
      if (parsedChats.length === 0) {
        const rawLegacy = window.sessionStorage.getItem(BR_SESSION_KEY);
        const legacy = rawLegacy ? JSON.parse(rawLegacy) : null;
        if (Array.isArray(legacy) && legacy.length > 0) {
          parsedChats = [{
            id: makeChatId(),
            title: summarizeChat(legacy),
            messages: legacy,
            updatedAt: Date.now(),
          }];
        }
      }

      if (parsedChats.length === 0) {
        parsedChats = [{ id: makeChatId(), title: 'New Back Room Chat', messages: [], updatedAt: Date.now() }];
      }

      const savedActive = window.localStorage.getItem(BR_ACTIVE_CHAT_KEY);
      const activeId = parsedChats.find((c) => c.id === savedActive)?.id || parsedChats[0].id;
      const activeChat = parsedChats.find((c) => c.id === activeId);
      const restoredMessages = Array.isArray(activeChat?.messages) ? activeChat.messages : [];

      setChatRows(parsedChats);
      setActiveChatId(activeId);
      setMessages(restoredMessages);

      const lastAssistant = [...restoredMessages].reverse().find((m) => m?.role === 'assistant' && typeof m?.content === 'string');
      if (lastAssistant?.content) {
        // Prevent immediate replay when re-opening panel.
        lastSpokenRef.current = lastAssistant.content;
      }
    } catch {
      // ignore corrupted local cache
    } finally {
      setSessionLoaded(true);
    }
  }, []);

  // Persist chat list and active chat pointer.
  useEffect(() => {
    if (!sessionLoaded) return;
    try {
      window.localStorage.setItem(BR_CHATS_KEY, JSON.stringify(chatRows));
      if (activeChatId) {
        window.localStorage.setItem(BR_ACTIVE_CHAT_KEY, activeChatId);
      }
      window.sessionStorage.setItem(BR_SESSION_KEY, JSON.stringify(messages));
    } catch {
      // storage may be unavailable; fail silently
    }
  }, [chatRows, activeChatId, messages, sessionLoaded]);

  // Keep the active row updated with latest message list.
  useEffect(() => {
    if (!sessionLoaded || !activeChatId) return;
    setChatRows((prev) => prev.map((row) => (
      row.id === activeChatId
        ? { ...row, messages, title: summarizeChat(messages), updatedAt: Date.now() }
        : row
    )));
  }, [messages, activeChatId, sessionLoaded]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(t);
  }, []);

  // Re-focus input every time Selene finishes responding
  useEffect(() => {
    if (!loading) {
      const t = setTimeout(() => {
        if (inputRef.current && document.body.contains(inputRef.current)) {
          inputRef.current.focus();
        }
      }, 200);
      return () => clearTimeout(t);
    }
  }, [loading]);

  // Persist TTS prefs
  useEffect(() => { window.localStorage.setItem('brTtsEnabled', String(ttsEnabled)); }, [ttsEnabled]);
  useEffect(() => { window.localStorage.setItem('brActiveVoices', JSON.stringify(activeVoices)); }, [activeVoices]);
  useEffect(() => { window.localStorage.setItem(BR_AUTO_GROUP_KEY, String(autoGroupEnabled)); }, [autoGroupEnabled]);
  useEffect(() => { window.localStorage.setItem(BR_AUTO_GROUP_MAX_KEY, String(autoGroupMaxTurns)); }, [autoGroupMaxTurns]);
  useEffect(() => { window.localStorage.setItem('brTheme', backroomTheme); }, [backroomTheme]);

  // Paste handler — captures Ctrl+V images from clipboard
  useEffect(() => {
    async function onPaste(e) {
      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter((it) => it.kind === 'file' && it.type.startsWith('image/'));
      if (imageItems.length === 0) return;
      e.preventDefault();
      for (const item of imageItems.slice(0, 3)) {
        const file = item.getAsFile();
        if (!file || file.size > BR_IMG_MAX_BYTES) continue;
        try {
          const dataUrl = await resizeImageForVision(file);
          const name = file.name && file.name !== 'image.png' ? file.name : `paste-${Date.now()}.jpg`;
          setPendingImages((prev) => [...prev, { dataUrl, name }]);
        } catch (err) {
          console.error('BR image paste failed:', err);
        }
      }
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  // Send an explicit session-start marker ONCE when the Back Room opens.
  // Previously this effect listed messages.length as a dep, which caused it
  // to re-fire after every user/error message — turning a single failure
  // into an unbounded retry loop. Now guarded by a one-shot ref so it can
  // never fire more than once per mount, regardless of dep changes.
  const sessionStartFiredRef = useRef(false);
  useEffect(() => {
    if (!sessionLoaded || sessionStartFiredRef.current) return;
    sessionStartFiredRef.current = true;
    backRoomChat({
      messages: [{ role: 'user', content: '__session_start__' }],
      activeVoices,
    }).then((result) => {
      const reply = result?.data?.reply || '';
      if (result?.data?.turnMode) setTurnModeLight(result.data.turnMode);
      if (Array.isArray(result?.data?.startupChecks)) {
        setStartupChecks(result.data.startupChecks);
        setShowStartupChecks(true);
        setStartupVerified(result?.data?.startupVerified === true);
      }
      if (reply && mountedRef.current) {
        setMessages((prev) => (prev.length > 0 ? prev : [{ role: 'assistant', content: reply }]));
      }
    }).catch((err) => {
      // Surface the error instead of silently failing — but only once,
      // because the circuit breaker will block further attempts.
      console.error('Back Room session start failed:', err);
      if (mountedRef.current) setTtsError(`Back room offline: ${err?.message || 'unknown error'}`);
    });
  }, [sessionLoaded, backRoomChat, activeVoices]);

  const refreshStartupChecks = useCallback(async () => {
    setRefreshingStartupChecks(true);
    try {
      const result = await backRoomChat({
        messages: [{ role: 'user', content: '__startup_refresh__' }],
        activeVoices,
      });
      if (result?.data?.turnMode) setTurnModeLight(result.data.turnMode);
      if (Array.isArray(result?.data?.startupChecks)) {
        setStartupChecks(result.data.startupChecks);
        setShowStartupChecks(true);
        setStartupVerified(result?.data?.startupVerified === true);
      }
    } catch (err) {
      setTtsError(`Startup refresh failed: ${err?.message || 'unknown error'}`);
    } finally {
      setRefreshingStartupChecks(false);
    }
  }, [backRoomChat, activeVoices]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Keep speechRateRef in sync so speak() always has the latest value
  useEffect(() => {
    speechRateRef.current = speechRate;
    window.localStorage.setItem('brSpeechRate', String(speechRate));
  }, [speechRate]);

  const SPEED_STEPS = [0.75, 1.0, 1.25, 1.5];
  const cycleSpeed = useCallback(() => {
    setSpeechRate((prev) => {
      const idx = SPEED_STEPS.indexOf(prev);
      return SPEED_STEPS[(idx + 1) % SPEED_STEPS.length];
    });
  }, []);

  // stopAll — stops audio, kills speak loop, clears loading
  const stopAll = useCallback(() => {
    cancelRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setTtsPlaying(false);
    setLoading(false);
    suppressAutoSpeakRef.current = false;
  }, []);

  // stopAudio — stops only the current audio clip, does NOT cancel the speak loop
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setTtsPlaying(false);
  }, []);

  const speak = useCallback(async (text) => {
    if (!text) return;
    cancelRef.current = false;
    setTtsPlaying(true);
    setTtsError('');
    console.log('[BR TTS] speak() called, activeVoices:', activeVoices, 'voiceMap:', voiceMap);
    const OPENAI_FALLBACK_VOICE_BY_SPEAKER = {
      SELENE: 'nova',
      ADAM: 'onyx',
      RACHEL: 'fable',
      SARA: 'alloy',
    };
    try {
      const charSegments = parseCharacterSegments(text);
      console.log('[BR TTS] charSegments:', charSegments);
      for (const charSeg of charSegments) {
        if (!mountedRef.current || cancelRef.current) break;
        if (!activeVoices[charSeg.speaker]) { console.log('[BR TTS] skipping', charSeg.speaker, '— not active'); continue; }
        const voice = voiceMap[charSeg.speaker] || voiceMap.SELENE;
        console.log('[BR TTS] speaking as', charSeg.speaker, 'voice:', voice);
        const emotionSegs = parseSeleneSegments(charSeg.text);
        for (const seg of emotionSegs) {
          if (!mountedRef.current || cancelRef.current) break;
          try {
            // Route by engine — synthesizeEaSpeech handles OpenAI tts-1-hd
            // (~10x cheaper than ElevenLabs); synthesizeSelenesSpeech is
            // ElevenLabs-only but preserves whisper/voice-specific tuning.
            const useOpenAi = voice.engine === 'openai';
            const synth = useOpenAi ? synthesizeEaSpeech : synthesizeSelenesSpeech;
            const payload = useOpenAi
              ? { text: seg.text, voice: voice.id }
              : { text: seg.text, whisper: seg.whisper, voiceId: voice.id };
            const r = await synth(payload);
            const audioBase64 = r?.data?.audioBase64 || '';
            const mimeType = r?.data?.mimeType || 'audio/mpeg';
            if (!audioBase64) {
              if (mountedRef.current) setTtsError(`No audio returned for ${charSeg.speaker} (${voice.engine} ${voice.id})`);
              continue;
            }
            stopAudio();
            if (cancelRef.current) break;
            await playSegment(audioBase64, mimeType, seg.whisper && !useOpenAi, speechRateRef.current);
          } catch (err) {
            const msg = err?.message || String(err);
            console.error('Back Room TTS segment failed:', { speaker: charSeg.speaker, engine: voice.engine, voiceId: voice.id, err });
            if (voice.engine === 'elevenlabs') {
              try {
                const fallbackVoice = OPENAI_FALLBACK_VOICE_BY_SPEAKER[charSeg.speaker] || 'nova';
                const fallback = await synthesizeEaSpeech({ text: seg.text, voice: fallbackVoice });
                const fbAudio = fallback?.data?.audioBase64 || '';
                const fbMime = fallback?.data?.mimeType || 'audio/mpeg';
                if (!fbAudio) throw new Error('No fallback audio returned');
                stopAudio();
                if (cancelRef.current) break;
                await playSegment(fbAudio, fbMime, false, speechRateRef.current);
                if (mountedRef.current) setTtsError(`ElevenLabs failed for ${charSeg.speaker}; using OpenAI fallback.`);
                continue;
              } catch (fallbackErr) {
                console.error('Back Room OpenAI fallback failed:', { speaker: charSeg.speaker, fallbackErr });
              }
            }
            if (mountedRef.current) setTtsError(`TTS failed (${charSeg.speaker}): ${msg}`);
          }
        }
      }
    } catch (err) {
      const msg = err?.message || String(err);
      console.error('Back Room TTS failed:', err);
      if (mountedRef.current) setTtsError(`TTS error: ${msg}`);
    } finally {
      if (mountedRef.current) setTtsPlaying(false);
    }
  }, [synthesizeSelenesSpeech, synthesizeEaSpeech, stopAudio, activeVoices, voiceMap]);

  // Auto-play latest assistant reply — suppressed during manual TTS chaining in sendAs
  useEffect(() => {
    if (!ttsEnabled || messages.length === 0) return;
    if (suppressAutoSpeakRef.current) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || !last.content) return;
    if (last.content === lastSpokenRef.current) return;
    lastSpokenRef.current = last.content;
    speak(last.content);
  }, [messages, ttsEnabled, speak]);

  async function sendAs(content, attachedImages = []) {
    if (!content && attachedImages.length === 0) return;
    if (loading) return;
    const displayContent = content || (attachedImages.length ? '📷 ' + attachedImages.map((img) => img.name).join(', ') : '');
    const userMsg = { role: 'user', content: displayContent, modelContent: sanitizeForModel(displayContent), _activeVoices: { ...activeVoices }, _images: attachedImages };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    cancelRef.current = false;
    setLoading(true);
    try {
      // Build history for model — user messages with images become content arrays
      let requestHistory = nextMessages.map((m) => {
        if (m.role !== 'user') return { role: 'assistant', content: m.content };
        const textContent = m.modelContent || sanitizeForModel(m.content);
        const imgs = Array.isArray(m._images) ? m._images : [];
        if (imgs.length === 0) return { role: 'user', content: textContent };
        return {
          role: 'user',
          content: [
            { type: 'text', text: textContent || 'What do you see in this image?' },
            ...imgs.map((img) => ({ type: 'image_url', image_url: { url: img.dataUrl, detail: 'auto' } })),
          ],
        };
      });
      const activeKeys = Object.keys(activeVoices).filter((k) => activeVoices[k]);
      const activeCount = activeKeys.length;
      const maxTurns = (autoGroupEnabled && activeCount > 1) ? autoGroupMaxTurns : 1;
      const isGroup = autoGroupEnabled && activeCount > 1 && maxTurns > 1;

      // Suppress the auto-speak useEffect during this whole flow so we control
      // TTS order manually — each voice finishes before the next one starts.
      if (isGroup && ttsEnabled) suppressAutoSpeakRef.current = true;

      const speakAndWait = async (text) => {
        if (!ttsEnabled || !mountedRef.current) return;
        lastSpokenRef.current = text;
        await speak(text);
      };

      // First turn: all active voices respond together (the opening round)
      // eslint-disable-next-line no-await-in-loop
      const firstResult = await backRoomChat({ _action: 'backRoomChat', messages: requestHistory, activeVoices });
      const firstReply = firstResult.data?.reply || '…';
      if (firstResult?.data?.turnMode) setTurnModeLight(firstResult.data.turnMode);
      if (!mountedRef.current || cancelRef.current) { suppressAutoSpeakRef.current = false; setLoading(false); return; }
      // eslint-disable-next-line no-await-in-loop
      await delayBotResponse();
      if (!mountedRef.current || cancelRef.current) { suppressAutoSpeakRef.current = false; setLoading(false); return; }
      setMessages((prev) => [...prev, { role: 'assistant', content: firstReply }]);
      // Store generated images if any
      if (firstResult.data?.generatedImages?.length > 0) {
        const messageIndex = messages.length + 1; // +1 for the user message we just added
        firstResult.data.generatedImages.forEach((img) => storeImage(messageIndex, img));
      }
      requestHistory.push({ role: 'assistant', content: firstReply });
      // eslint-disable-next-line no-await-in-loop
      await speakAndWait(firstReply);

      // Subsequent turns: voices react to each other — organic back-and-forth
      for (let turn = 1; turn < maxTurns; turn++) {
        if (!autoGroupEnabled || activeCount < 2) break;
        if (!mountedRef.current || cancelRef.current) break;

        // Pick the next voice in rotation
        const nextKey = activeKeys[turn % activeKeys.length];
        const nextName = nextKey.charAt(0) + nextKey.slice(1).toLowerCase();
        const singleVoice = Object.fromEntries(activeKeys.map((k) => [k, k === nextKey]));

        // Let the voice react to what was just said — no rigid instruction, just natural
        requestHistory.push({
          role: 'user',
          content: `[${nextName} reacts]`,
        });

        // eslint-disable-next-line no-await-in-loop
        const result = await backRoomChat({ _action: 'backRoomChat', messages: requestHistory, activeVoices: singleVoice });
        const reply = result.data?.reply || '…';
        if (result?.data?.turnMode) setTurnModeLight(result.data.turnMode);
        if (!mountedRef.current || cancelRef.current) { suppressAutoSpeakRef.current = false; setLoading(false); return; }

        // eslint-disable-next-line no-await-in-loop
        await delayBotResponse();
        if (!mountedRef.current || cancelRef.current) { suppressAutoSpeakRef.current = false; setLoading(false); return; }
        setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
        // Store generated images if any
        if (result.data?.generatedImages?.length > 0) {
          const messageIndex = messages.length + (turn * 2) + 1; // Account for [reaction] and response messages
          result.data.generatedImages.forEach((img) => storeImage(messageIndex, img));
        }
        requestHistory.push({ role: 'assistant', content: reply });
        // eslint-disable-next-line no-await-in-loop
        await speakAndWait(reply);
      }

      suppressAutoSpeakRef.current = false;
    } catch (err) {
      suppressAutoSpeakRef.current = false;
      if (!mountedRef.current) return;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `[Something went wrong: ${err.message}]` },
      ]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  async function handleSend() {
    const content = input.trim();
    const imgs = [...pendingImages];
    if (!content && imgs.length === 0) return;
    if (loading) return;
    if (backroomDown) return; // Circuit breaker tripped — block sends.
    setInput('');
    setPendingImages([]);
    await sendAs(content, imgs);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function startNewChat() {
    const next = {
      id: makeChatId(),
      title: 'New Back Room Chat',
      messages: [],
      updatedAt: Date.now(),
    };
    stopAudio();
    setChatRows((prev) => [next, ...prev]);
    setActiveChatId(next.id);
    setMessages([]);
    lastSpokenRef.current = '';
    setShowChatHistory(false);
  }

  function openChat(chatId) {
    const target = chatRows.find((row) => row.id === chatId);
    if (!target) return;
    stopAudio();
    setActiveChatId(chatId);
    setMessages(Array.isArray(target.messages) ? target.messages : []);
    const lastAssistant = [...(target.messages || [])].reverse().find((m) => m?.role === 'assistant' && typeof m?.content === 'string');
    lastSpokenRef.current = lastAssistant?.content || '';
    setShowChatHistory(false);
  }

  function deleteChat(chatId) {
    stopAudio();
    setChatRows((prev) => {
      const remaining = prev.filter((row) => row.id !== chatId);
      if (remaining.length === 0) {
        const fresh = { id: makeChatId(), title: 'New Back Room Chat', messages: [], updatedAt: Date.now() };
        setActiveChatId(fresh.id);
        setMessages([]);
        lastSpokenRef.current = '';
        return [fresh];
      }

      if (chatId === activeChatId) {
        const next = remaining[0];
        setActiveChatId(next.id);
        setMessages(Array.isArray(next.messages) ? next.messages : []);
        const lastAssistant = [...(next.messages || [])].reverse().find((m) => m?.role === 'assistant' && typeof m?.content === 'string');
        lastSpokenRef.current = lastAssistant?.content || '';
      }
      return remaining;
    });
  }

  return (
    <div
      onClick={fullPage ? undefined : onClose}
      style={{
        position: fullPage ? 'relative' : 'fixed',
        inset: 0,
        zIndex: 999,
        backgroundColor: fullPage ? '#0e0b08' : 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        justifyContent: fullPage ? 'stretch' : 'flex-end',
        height: '100dvh',
      }}
    >
      <div
        onClick={fullPage ? undefined : (e) => e.stopPropagation()}
        onDragOver={(e) => { e.preventDefault(); if (!e.dataTransfer.types.includes('Files')) return; setChatDragActive(true); }}
        onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setChatDragActive(false); }}
        onDrop={handleChatDrop}
        style={{
          width: fullPage ? '100%' : (isMobile ? '100%' : '420px'),
          height: '100%',
          backgroundColor: '#0e0b08',
          filter: BACKROOM_THEME_FILTERS[backroomTheme === 'auto' ? 'ember' : backroomTheme] || 'none',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: fullPage || isMobile ? 'none' : '1px solid #2a1e0a',
          boxShadow: fullPage ? 'none' : '-8px 0 40px rgba(0, 0, 0, 0.6)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {chatDragActive && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 100,
            backgroundColor: 'rgba(196, 125, 46, 0.07)',
            border: '2px dashed #c47d2e',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            <div style={{ color: '#c47d2e', fontSize: '22px', marginBottom: '6px' }}>📂</div>
            <div style={{ color: '#c47d2e', fontSize: '14px', fontWeight: 600 }}>Drop files here</div>
            <div style={{ color: '#8a6a48', fontSize: '11px', marginTop: '4px' }}>text · code · data · images</div>
            <div style={{ color: '#8a6a48', fontSize: '11px', marginTop: '2px' }}>video · screenshots</div>
          </div>
        )}
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px 14px',
            borderBottom: '1px solid #1e1508',
            backgroundColor: '#120f09',
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{
              fontSize: '15px',
              fontWeight: 700,
              color: '#c47d2e',
              letterSpacing: '0.4px',
            }}>
              Back Room
            </div>
            <div style={{ fontSize: '10px', color: '#8a6a48', marginTop: '2px', letterSpacing: '0.3px' }}>
              Private · Session only · No logs
            </div>
          </div>

          {/* TTS controls + close */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* New chat */}
            <button
              onClick={startNewChat}
              disabled={loading}
              style={{
                background: 'none',
                border: '1px solid #2a1e0a',
                borderRadius: '6px',
                color: loading ? '#2a1e0a' : '#5a4a38',
                fontSize: '13px',
                cursor: loading ? 'default' : 'pointer',
                padding: '3px 8px',
                lineHeight: 1,
              }}
              title="New chat"
            >
              +
            </button>

            {/* Chat history */}
            <button
              onClick={() => setShowChatHistory((v) => !v)}
              style={{
                background: 'none',
                border: '1px solid #2a1e0a',
                borderRadius: '6px',
                color: showChatHistory ? '#c47d2e' : '#5a4a38',
                fontSize: '13px',
                cursor: 'pointer',
                padding: '3px 8px',
                lineHeight: 1,
              }}
              title="Chat history"
            >
              🕘
            </button>

            <select
              value={backroomTheme}
              onChange={(e) => setBackroomTheme(e.target.value)}
              style={{
                background: 'none',
                border: '1px solid #2a1e0a',
                borderRadius: '6px',
                color: '#8a6a48',
                fontSize: '11px',
                cursor: 'pointer',
                padding: '3px 6px',
              }}
              title="Back room color theme"
            >
              <option value="auto">Auto</option>
              <option value="ember">Ember</option>
              <option value="ocean">Ocean</option>
              <option value="forest">Forest</option>
              <option value="plum">Plum</option>
            </select>

            {/* Browse brain_drain notes */}
            <button
              onClick={() => sendAs('List what\'s in my brain_drain repo so I can see what\'s there.')}
              disabled={loading}
              style={{
                background: 'none',
                border: '1px solid #2a1e0a',
                borderRadius: '6px',
                color: loading ? '#2a1e0a' : '#5a4a38',
                fontSize: '15px',
                cursor: loading ? 'default' : 'pointer',
                padding: '3px 8px',
                lineHeight: 1,
              }}
              title="Browse brain_drain notes"
            >
              📂
            </button>
            {/* Stop button — visible when loading (thinking) or playing audio */}
            {(loading || ttsPlaying) && (
              <button
                onClick={stopAll}
                style={{ background: 'none', border: '1px solid #3a2210', borderRadius: '6px', color: '#c47d2e', fontSize: '13px', cursor: 'pointer', padding: '3px 8px' }}
                title={loading ? 'Stop response' : 'Stop speaking'}
              >
                ■
              </button>
            )}

            {/* Voice on/off pills — all toggleable */}
            {Object.entries(voiceMap).map(([key, v]) => (
              <button
                key={key}
                onClick={() => setActiveVoices((prev) => {
                  const next = { ...prev, [key]: !prev[key] };
                  if (!next.SELENE && !next.ADAM && !next.RACHEL && !next.SARA) next[key] = true;
                  return next;
                })}
                style={{
                  background: 'none',
                  border: `1px solid ${activeVoices[key] ? '#5a3a18' : '#2a1a08'}`,
                  borderRadius: '12px',
                  color: activeVoices[key] ? v.color : '#3a2a1a',
                  fontSize: '11px',
                  padding: '2px 9px',
                  cursor: 'pointer',
                  fontWeight: activeVoices[key] ? 600 : 400,
                  letterSpacing: '0.2px',
                }}
                title={`${v.label} — ${activeVoices[key] ? 'ON — click to disable' : 'OFF — click to enable'}`}
              >
                {v.label}
              </button>
            ))}

            {/* Mute/unmute toggle */}
            <button
              onClick={() => { setTtsEnabled(v => !v); if (ttsPlaying) stopAudio(); }}
              style={{
                background: 'none', border: '1px solid #2a1e0a', borderRadius: '6px',
                color: ttsEnabled ? '#c47d2e' : '#3a2a1a',
                fontSize: '16px', cursor: 'pointer', padding: '3px 8px',
              }}
              title={ttsEnabled ? 'Voice on — click to mute' : 'Voice off — click to enable'}
            >
              {ttsEnabled ? '🔊' : '🔇'}
            </button>

            <button
              onClick={() => setAdminOpen((v) => !v)}
              style={{
                background: 'none', border: '1px solid #2a1e0a', borderRadius: '6px',
                color: adminOpen ? '#d9a15d' : '#6a5842',
                fontSize: '11px', cursor: 'pointer', padding: '3px 8px',
              }}
              title="Back Room admin — memory, voices, avatar"
            >
              Admin
            </button>

            <button
              onClick={() => setAutoGroupEnabled((v) => !v)}
              style={{
                background: 'none', border: '1px solid #2a1e0a', borderRadius: '6px',
                color: autoGroupEnabled ? '#c47d2e' : '#5a4a38',
                fontSize: '11px', cursor: 'pointer', padding: '3px 8px',
              }}
              title="Auto group flow: continue multi-voice exchanges up to the max-turn cap"
            >
              Auto {autoGroupEnabled ? 'ON' : 'OFF'}
            </button>

            <select
              value={autoGroupMaxTurns}
              onChange={(e) => setAutoGroupMaxTurns(Math.min(6, Math.max(1, Number(e.target.value) || 3)))}
              style={{
                backgroundColor: '#120f09', border: '1px solid #2a1e0a', borderRadius: '6px',
                color: '#8a6a48', fontSize: '11px', padding: '3px 6px',
              }}
              title="Maximum auto turns for multi-voice flow"
            >
              <option value={1}>1 turn</option>
              <option value={2}>2 turns</option>
              <option value={3}>3 turns</option>
              <option value={4}>4 turns</option>
              <option value={5}>5 turns</option>
              <option value={6}>6 turns</option>
            </select>

            {/* Close */}
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#5a4a38', fontSize: fullPage ? '14px' : '22px', cursor: 'pointer', padding: '2px 6px', lineHeight: 1, letterSpacing: fullPage ? '0.4px' : undefined, textTransform: fullPage ? 'uppercase' : undefined }}
              title={fullPage ? 'Return to Executive Assistant' : 'Close Back Room'}
            >
              {fullPage ? 'EA ←' : '×'}
            </button>
          </div>
        </div>

        {backroomDown && (
          <div style={{
            padding: '10px 14px',
            backgroundColor: '#2a0d08',
            borderBottom: '1px solid #6a2010',
            color: '#f0a070',
            fontSize: '12px',
            lineHeight: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}>
            <strong>⚠ Back room offline — calls disabled.</strong>
            <span>The localhost server at 127.0.0.1:8787 isn't responding. Restart it, then refresh this page. (Sends are blocked until then to prevent retry loops.)</span>
          </div>
        )}
        {ttsError && (
          <div style={{
            padding: '6px 14px',
            backgroundColor: '#1a0a06',
            borderBottom: '1px solid #3a1208',
            color: '#d38c6c',
            fontSize: '11px',
            lineHeight: 1.4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '8px',
          }}>
            <span>⚠ {ttsError}</span>
            <button onClick={() => setTtsError('')}
              style={{ background: 'none', border: 'none', color: '#7a3a28', cursor: 'pointer', fontSize: '14px', lineHeight: 1, flexShrink: 0 }}>×</button>
          </div>
        )}
        <div style={{
          padding: '6px 14px',
          borderBottom: '1px solid #1e1508',
          backgroundColor: '#120f09',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: '#9eb6d5', fontWeight: 600 }}>Turn Mode</span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                borderRadius: '999px',
                fontSize: '11px',
                color: '#0f1720',
                background: turnModeLight === 'business' ? '#f39c12' : (turnModeLight === 'personal' ? '#2ecc71' : '#6c7a89'),
                fontWeight: 700,
              }}
            >
              {turnModeLight === 'business' ? 'Business' : (turnModeLight === 'personal' ? 'Personal' : 'Unknown')}
            </span>
          </div>
          {Array.isArray(startupChecks) && startupChecks.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showStartupChecks ? '6px' : '0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#9eb6d5', fontWeight: 600 }}>Session Startup File Read Checks</div>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '10px',
                      color: '#0f1720',
                      background: startupVerified === true ? '#2ecc71' : '#f39c12',
                      fontWeight: 700,
                    }}
                    title={startupVerified === true ? 'All startup checks passed.' : 'One or more startup checks are missing or failed.'}
                  >
                    {startupVerified === true ? 'VERIFIED' : 'PARTIAL'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={refreshStartupChecks}
                    disabled={refreshingStartupChecks}
                    style={{ background: 'none', border: '1px solid #2a1e0a', borderRadius: '6px', color: '#8a6a48', fontSize: '10px', cursor: 'pointer', padding: '2px 6px', opacity: refreshingStartupChecks ? 0.6 : 1 }}
                    title="Re-run startup file-read checks"
                  >
                    {refreshingStartupChecks ? 'Checking…' : 'Re-run'}
                  </button>
                  <button
                    onClick={() => setShowStartupChecks((v) => !v)}
                    style={{ background: 'none', border: '1px solid #2a1e0a', borderRadius: '6px', color: '#8a6a48', fontSize: '10px', cursor: 'pointer', padding: '2px 6px' }}
                    title={showStartupChecks ? 'Hide checklist' : 'Show checklist'}
                  >
                    {showStartupChecks ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              {showStartupChecks && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {startupChecks.map((c) => (
                    <div key={c.key} style={{ fontSize: '11px', color: c.ok ? '#9fe8b7' : '#f0a070', display: 'flex', gap: '6px' }}>
                      <span>{c.ok ? '✓' : '•'}</span>
                      <span>
                        {c.label}
                        {c.detail ? ` (${c.detail})` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {adminOpen && (
          <BackRoomAdminPanel
            debugData={debugData}
            voiceMap={voiceMap}
            voiceCatalog={voiceCatalog}
            lastRecap={lastRecap}
            activeVoices={activeVoices}
            onSetActiveVoices={setActiveVoices}
            autoGroupEnabled={autoGroupEnabled}
            onSetAutoGroupEnabled={setAutoGroupEnabled}
            autoGroupMaxTurns={autoGroupMaxTurns}
            onSetAutoGroupMaxTurns={setAutoGroupMaxTurns}
            onSaveVoices={handleSaveVoices}
            onSaveMemoryField={handleSaveMemoryField}
            onTestTts={(voiceId) => speak(`Hello Rodger, voice test for ${voiceId} is working.`)}
            saving={adminSaving}
            saveMsg={adminSaveMsg}
            speechRate={speechRate}
            onSpeechRateChange={(v) => setSpeechRate(v)}
          />
        )}

        {showChatHistory && (
          <div
            style={{
              borderBottom: '1px solid #1e1508',
              backgroundColor: '#120f09',
              maxHeight: '190px',
              overflowY: 'auto',
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            {chatRows
              .slice()
              .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
              .map((row) => (
                <div
                  key={row.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    border: row.id === activeChatId ? '1px solid #5a3a18' : '1px solid #2a1a08',
                    backgroundColor: row.id === activeChatId ? '#1b1208' : '#151009',
                    borderRadius: '7px',
                    padding: '6px 8px',
                  }}
                >
                  <button
                    onClick={() => openChat(row.id)}
                    style={{
                      flex: 1,
                      background: 'none',
                      border: 'none',
                      color: row.id === activeChatId ? '#d9a15d' : '#8a6a48',
                      fontSize: '12px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={row.title || 'Untitled chat'}
                  >
                    {row.title || 'Untitled chat'}
                  </button>
                  <button
                    onClick={() => deleteChat(row.id)}
                    style={{
                      background: 'none',
                      border: '1px solid #3a2210',
                      borderRadius: '5px',
                      color: '#b87b3e',
                      fontSize: '11px',
                      cursor: 'pointer',
                      padding: '2px 6px',
                    }}
                    title="Delete chat"
                  >
                    Delete
                  </button>
                </div>
              ))}
          </div>
        )}

        {/* Content row: chat column (left) + crew sidebar (right, fullPage only) */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', minHeight: 0, overflow: 'hidden' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>


        {/* Messages */}
        <div
          style={{
            order: 1,
            flex: 1,
            overflowY: 'auto',
            padding: '20px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
          }}
        >
          {messages.length === 0 && (
            <div style={{
              color: '#3a2e20',
              fontSize: '13px',
              textAlign: 'center',
              marginTop: '40px',
              fontStyle: 'italic',
              lineHeight: 1.6,
            }}>
              No one's listening in here but you.
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              style={
                msg.role === 'user'
                  ? {
                      alignSelf: 'flex-end',
                      maxWidth: '85%',
                      backgroundColor: '#1c1408',
                      border: '1px solid #2e1e08',
                      borderRadius: '16px 16px 4px 16px',
                      padding: '10px 14px',
                      color: '#e0c890',
                      fontSize: '14px',
                      lineHeight: '1.65',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }
                  : {
                      alignSelf: 'flex-start',
                      maxWidth: '90%',
                      color: '#c8a870',
                      fontSize: '14px',
                      lineHeight: '1.75',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      paddingLeft: '4px',
                    }
              }
            >
              {renderMarkdown(typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content))}
              {msg.role === 'assistant' && getImage(i) && (
                <GeneratedImage imageData={getImage(i)} />
              )}
            </div>
          ))}

          {loading && (
            <div style={{
              alignSelf: 'flex-start',
              paddingLeft: '4px',
            }}>
              <LoadingDots />
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div
          style={{
            order: 3,
            borderTop: '1px solid #1e1508',
            backgroundColor: '#120f09',
            padding: '12px 16px',
            paddingBottom: 'max(12px, calc(8px + env(safe-area-inset-bottom, 0px)))',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'flex-end',
            flexShrink: 0,
          }}
        >
          {pendingImages.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px', width: '100%' }}>
              {pendingImages.map((img, idx) => (
                <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={img.dataUrl}
                    alt={img.name}
                    style={{ height: '56px', width: '56px', objectFit: 'cover', borderRadius: '6px', border: '1px solid rgba(196,125,46,0.4)', display: 'block' }}
                  />
                  <button
                    onClick={() => setPendingImages((prev) => prev.filter((_, i) => i !== idx))}
                    style={{ position: 'absolute', top: '-5px', right: '-5px', width: '16px', height: '16px', borderRadius: '50%', background: '#3a2210', border: '1px solid #c47d2e', color: '#c47d2e', fontSize: '10px', lineHeight: '14px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Remove image"
                  >×</button>
                </div>
              ))}
            </div>
          )}
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={pendingImages.length > 0 ? 'Add a note about the image, or just hit send…' : 'Say anything… (Ctrl+V to paste a screenshot)'}
            rows={3}
            disabled={loading}
            style={{
              flex: 1,
              backgroundColor: '#1a1208',
              border: '1px solid #2a1a08',
              borderRadius: '10px',
              color: '#e0c890',
              fontSize: '15px',
              padding: '10px 12px',
              resize: 'none',
              outline: 'none',
              lineHeight: '1.5',
              fontFamily: 'inherit',
              maxHeight: '120px',
              overflowY: 'auto',
              WebkitAppearance: 'none',
              appearance: 'none',
            }}
          />
          <button
           onClick={() => { if (sttRecording) { stopLocalStt(); } else { startLocalStt(); } }}
           disabled={loading || (!localSttSupported && !sttRecording)}
            style={{
              width: '42px',
              height: '42px',
              minWidth: '42px',
             backgroundColor: sttRecording ? '#8b0000' : (loading || !localSttSupported ? '#1a1208' : '#163a2a'),
              border: '1px solid #3a2210',
              borderRadius: '10px',
             color: sttRecording ? '#fff' : (loading || !localSttSupported ? '#3a2a18' : '#e0f0e8'),
              fontSize: '18px',
             cursor: loading || !localSttSupported ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'background-color 0.15s, color 0.15s',
            }}
           title={localSttSupported ? (sttRecording ? 'Stop recording' : 'Record (Local STT)') : 'Local STT unsupported — run in Chrome/Edge or set up VOSK service'}
          >
           {sttRecording ? '●' : '🎙'}
          </button>

          <button
           onClick={handleSend}
           disabled={loading || !input.trim()}
           style={{
             width: '42px',
             height: '42px',
             minWidth: '42px',
             backgroundColor: loading || !input.trim() ? '#1a1208' : '#5a3a18',
             border: '1px solid #3a2210',
             borderRadius: '10px',
             color: loading || !input.trim() ? '#3a2a18' : '#e0c890',
             fontSize: '18px',
             cursor: loading || !input.trim() ? 'default' : 'pointer',
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             flexShrink: 0,
             transition: 'background-color 0.15s, color 0.15s',
           }}
           title="Send"
          >
           ↑
          </button>
        </div>
          </div>
          {fullPage && (
            <CrewRoster
              voiceMap={voiceMap}
              voiceCatalog={voiceCatalog}
              activeVoices={activeVoices}
              onToggleVoice={(key) => setActiveVoices((prev) => {
                const next = { ...prev, [key]: !prev[key] };
                if (!next.SELENE && !next.ADAM && !next.RACHEL && !next.SARA) next[key] = true;
                return next;
              })}
              onSoloVoice={(key) => setActiveVoices({
                SELENE: key === 'SELENE',
                ADAM: key === 'ADAM',
                RACHEL: key === 'RACHEL',
                SARA: key === 'SARA',
              })}
              onPickVoice={(key, id, engine) => setCharacterVoice(key, id, engine)}
              onTestVoice={(key) => testCharacterVoice(key)}
              onSaveDefaults={saveVoiceDefaultsToServer}
              saveMsg={adminSaveMsg}
              saving={adminSaving}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// File-drop helpers for Back Room chat panel
// ─────────────────────────────────────────────────────────────
const TEXT_EXTS = new Set([
  'txt', 'md', 'markdown', 'json', 'csv', 'js', 'ts', 'jsx', 'tsx',
  'py', 'html', 'css', 'xml', 'yaml', 'yml', 'sh', 'ps1', 'env',
  'log', 'sql', 'c', 'cpp', 'h', 'java', 'rb', 'go', 'rs', 'toml', 'ini',
]);
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']);
const VIDEO_EXTS = new Set(['mp4', 'webm', 'mov', 'm4v', 'avi', 'mkv']);
function getFileExt(name) { return (name || '').split('.').pop().toLowerCase(); }
function isTextFile(f) {
  if (!f) return false;
  if (TEXT_EXTS.has(getFileExt(f.name))) return true;
  return f.type.startsWith('text/') || f.type === 'application/json';
}
function isImageFile(f) {
  return !!f && (f.type.startsWith('image/') || IMAGE_EXTS.has(getFileExt(f.name)));
}
function isVideoFile(f) {
  return !!f && (f.type.startsWith('video/') || VIDEO_EXTS.has(getFileExt(f.name)));
}
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Read failed'));
    reader.readAsText(file);
  });
}
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    // Keep previews lightweight and avoid huge prompt payloads.
    if ((file?.size || 0) > 12 * 1024 * 1024) {
      reject(new Error('File too large for preview'));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => resolve(String(e.target.result || ''));
    reader.onerror = () => reject(new Error('Read failed'));
    reader.readAsDataURL(file);
  });
}

// ─────────────────────────────────────────────────────────────
// CrewRoster — right-side panel showing the four AIs with per-AI
// on/off toggle, voice picker (ElevenLabs / OpenAI), solo, and test.
// Mirrors the front room's right sidebar pattern but keeps the
// copper/sienna palette so each room stays visually distinct.
// ─────────────────────────────────────────────────────────────
function CrewRoster({
  voiceMap,
  voiceCatalog,
  activeVoices,
  onToggleVoice,
  onSoloVoice,
  onPickVoice,
  onTestVoice,
  onSaveDefaults,
  saveMsg,
  saving,
}) {
  const order = ['SELENE', 'RACHEL', 'SARA', 'ADAM'];
  const openai = (Array.isArray(voiceCatalog?.openai) && voiceCatalog.openai.length > 0)
    ? voiceCatalog.openai
    : FALLBACK_VOICE_CATALOG.openai;

  return (
    <aside
      style={{
        width: '240px',
        flexShrink: 0,
        borderLeft: '1px solid #1e1508',
        backgroundColor: '#0c0a07',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 10px',
        gap: '10px',
      }}
      aria-label="Back Room crew roster"
    >
      <div style={{
        fontSize: '10px',
        fontWeight: 700,
        color: '#c47d2e',
        letterSpacing: '0.8px',
        textTransform: 'uppercase',
        padding: '0 4px 4px',
        borderBottom: '1px solid #1e1508',
      }}>
        Crew
      </div>

      {order.map((key) => {
        const v = voiceMap[key];
        if (!v) return null;
        const isOn = !!activeVoices[key];
        const currentValue = `${v.engine}:${v.id}`;
        return (
          <div
            key={key}
            style={{
              border: `1px solid ${isOn ? '#3a2618' : '#1e1508'}`,
              backgroundColor: isOn ? '#15100a' : '#0e0b08',
              borderRadius: '8px',
              padding: '8px 9px',
              display: 'flex',
              flexDirection: 'column',
              gap: '7px',
            }}
          >
            {/* Row 1: name + on/off toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{
                fontSize: '12px',
                fontWeight: 700,
                color: isOn ? v.color : '#5a4a38',
                letterSpacing: '0.3px',
              }}>
                {v.label}
              </span>
              <button
                onClick={() => onToggleVoice(key)}
                style={{
                  background: isOn ? '#5a3a18' : 'transparent',
                  border: `1px solid ${isOn ? v.color : '#2a1e0a'}`,
                  borderRadius: '10px',
                  color: isOn ? '#e0c890' : '#5a4a38',
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  padding: '2px 7px',
                  cursor: 'pointer',
                }}
                title={isOn ? 'On — click to mute' : 'Off — click to enable'}
              >
                {isOn ? 'ON' : 'OFF'}
              </button>
            </div>

            {/* Row 2: voice picker (engine + voice in one dropdown) */}
            <select
              value={currentValue}
              onChange={(e) => {
                const [engine, id] = String(e.target.value).split(':');
                if (engine && id) onPickVoice(key, id, engine);
              }}
              style={{
                width: '100%',
                backgroundColor: '#120f09',
                border: '1px solid #2a1e0a',
                borderRadius: '6px',
                color: '#bda07a',
                fontSize: '11px',
                padding: '4px 6px',
              }}
              title="Pick a voice for this character"
            >
              <optgroup label="ElevenLabs (premium)">
                {(CHARACTER_VOICES[key] || []).length > 0
                  ? (CHARACTER_VOICES[key] || []).map((opt) => (
                      <option key={`el-${opt.id}`} value={`elevenlabs:${opt.id}`}>{opt.label}</option>
                    ))
                  : <option disabled value="">— no voices configured —</option>
                }
              </optgroup>
              <optgroup label="OpenAI (cheap)">
                {openai.map((opt) => (
                  <option key={`oa-${opt.id}`} value={`openai:${opt.id}`}>{opt.label}</option>
                ))}
              </optgroup>
            </select>

            {/* Row 3: solo + test */}
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                onClick={() => onSoloVoice(key)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: '1px solid #2a1e0a',
                  borderRadius: '6px',
                  color: '#8a6a48',
                  fontSize: '10px',
                  letterSpacing: '0.3px',
                  padding: '3px 6px',
                  cursor: 'pointer',
                }}
                title="Mute the others and talk only to this one"
              >
                Solo
              </button>
              <button
                onClick={() => onTestVoice(key)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: '1px solid #2a1e0a',
                  borderRadius: '6px',
                  color: '#8a6a48',
                  fontSize: '10px',
                  letterSpacing: '0.3px',
                  padding: '3px 6px',
                  cursor: 'pointer',
                }}
                title="Speak a sample with this voice"
              >
                Test
              </button>
            </div>
          </div>
        );
      })}

      <div style={{
        fontSize: '9px',
        color: '#5a4030',
        lineHeight: 1.5,
        padding: '4px 4px 0',
        marginTop: 'auto',
      }}>
        OpenAI voices run ~10× cheaper than ElevenLabs.
      </div>
      {onSaveDefaults && (
        <button
          onClick={onSaveDefaults}
          disabled={saving}
          style={{
            marginTop: '8px',
            width: '100%',
            background: 'transparent',
            border: `1px solid ${saveMsg ? '#4a7a4a' : '#2a1e0a'}`,
            borderRadius: '6px',
            color: saveMsg ? '#82c882' : '#8a6a48',
            fontSize: '10px',
            letterSpacing: '0.3px',
            padding: '5px 6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
          title="Save current voice selections as the default for next session"
        >
          {saving ? 'Saving…' : (saveMsg || 'Set as Default')}
        </button>
      )}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────
// BackRoomAdminPanel — tabbed admin: Memory · Voices · Avatar
// ─────────────────────────────────────────────────────────────
function BackRoomAdminPanel({
  debugData,
  voiceMap,
  voiceCatalog,
  lastRecap,
  activeVoices,
  onSetActiveVoices,
  autoGroupEnabled,
  onSetAutoGroupEnabled,
  autoGroupMaxTurns,
  onSetAutoGroupMaxTurns,
  onSaveVoices,
  onSaveMemoryField,
  onTestTts,
  saving,
  saveMsg,
  speechRate = 1,
  onSpeechRateChange,
}) {
  const [tab, setTab] = React.useState('memory');
  const MEM_FIELDS = [
    { key: 'about_rodger',  label: 'About Rodger' },
    { key: 'preferences',   label: 'Preferences' },
    { key: 'side_projects', label: 'Side projects' },
    { key: 'notes',         label: 'Notes' },
    { key: 'last_session',  label: 'Last session' },
  ];
  const [memEdits, setMemEdits] = React.useState(
    () => Object.fromEntries(MEM_FIELDS.map(({ key }) => [key, debugData?.memory?.[key] || '']))
  );
  React.useEffect(() => {
    if (debugData?.memory) {
      setMemEdits(Object.fromEntries(MEM_FIELDS.map(({ key }) => [key, debugData.memory[key] || ''])));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debugData]);
  const [voiceEdits, setVoiceEdits] = React.useState({
    SELENE: `${voiceMap?.SELENE?.engine || 'elevenlabs'}:${voiceMap?.SELENE?.id || ''}`,
    ADAM:   `${voiceMap?.ADAM?.engine || 'elevenlabs'}:${voiceMap?.ADAM?.id || ''}`,
    RACHEL: `${voiceMap?.RACHEL?.engine || 'elevenlabs'}:${voiceMap?.RACHEL?.id || ''}`,
    SARA:   `${voiceMap?.SARA?.engine || 'elevenlabs'}:${voiceMap?.SARA?.id || ''}`,
  });
  React.useEffect(() => {
    setVoiceEdits({
      SELENE: `${voiceMap?.SELENE?.engine || 'elevenlabs'}:${voiceMap?.SELENE?.id || ''}`,
      ADAM:   `${voiceMap?.ADAM?.engine || 'elevenlabs'}:${voiceMap?.ADAM?.id || ''}`,
      RACHEL: `${voiceMap?.RACHEL?.engine || 'elevenlabs'}:${voiceMap?.RACHEL?.id || ''}`,
      SARA:   `${voiceMap?.SARA?.engine || 'elevenlabs'}:${voiceMap?.SARA?.id || ''}`,
    });
  }, [voiceMap]);
  const VCOL = { SELENE: '#c47d2e', ADAM: '#6a9abf', RACHEL: '#7abf8a', SARA: '#9aa6d9' };
  const fld = {
    width: '100%', backgroundColor: '#17120c', border: '1px solid #2a1a08',
    borderRadius: '6px', color: '#d9c2a0', fontSize: '12px', padding: '6px 8px',
    fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  };
  const btn = (disabled) => ({
    marginTop: '3px', background: disabled ? '#1a1208' : '#2a1808',
    border: '1px solid #5a3a18', borderRadius: '5px',
    color: disabled ? '#4a3020' : '#c47d2e', fontSize: '11px',
    cursor: disabled ? 'default' : 'pointer', padding: '2px 10px',
  });
  const openAiDefaults = React.useMemo(() => {
    const picks = Array.isArray(voiceCatalog?.openai) ? voiceCatalog.openai : [];
    return {
      SELENE: `openai:${picks[4]?.id || 'nova'}`,
      ADAM: `openai:${picks[3]?.id || 'onyx'}`,
      RACHEL: `openai:${picks[2]?.id || 'fable'}`,
      SARA: `openai:${picks[0]?.id || 'alloy'}`,
    };
  }, [voiceCatalog]);
  return (
    <div style={{ borderBottom: '1px solid #1e1508', backgroundColor: '#100d08', fontSize: '12px', color: '#bda07a' }}>
      <div style={{ display: 'flex', borderBottom: '1px solid #1e1508', padding: '0 8px', gap: '2px' }}>
        {['memory', 'voices', 'avatar'].map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: 'none', border: 'none',
            borderBottom: tab === t ? '2px solid #c47d2e' : '2px solid transparent',
            color: tab === t ? '#d9a15d' : '#6a5040', fontSize: '11px',
            fontWeight: tab === t ? 700 : 400, cursor: 'pointer',
            padding: '5px 10px', letterSpacing: '0.4px', textTransform: 'uppercase',
          }}>{t}</button>
        ))}
      </div>
      <div style={{ padding: '10px 12px', maxHeight: '300px', overflowY: 'auto' }}>
        {saveMsg && (
          <div style={{ color: saveMsg.includes('failed') || saveMsg.includes('Failed') ? '#d38c6c' : '#7abf8a', fontSize: '11px', marginBottom: '8px' }}>
            {saveMsg}
          </div>
        )}
        {tab === 'memory' && (
          <div>
            {lastRecap && (
              <div style={{ marginBottom: '12px', padding: '7px 10px', borderRadius: '6px', border: '1px solid #3a2a18', background: '#120e09' }}>
                <div style={{ color: '#d9a15d', fontSize: '10px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: '3px' }}>Last session recap</div>
                <div style={{ color: '#bda07a', fontSize: '11px' }}>{lastRecap.personas?.join(', ')} — {lastRecap.at}</div>
              </div>
            )}
            {!lastRecap && (
              <div style={{ color: '#5a4838', fontSize: '11px', marginBottom: '10px' }}>No session recap written yet. One fires automatically after 30 min of inactivity.</div>
            )}
            {MEM_FIELDS.map(({ key, label }) => (
              <div key={key} style={{ marginBottom: '12px' }}>
                <div style={{ color: '#d9a15d', fontWeight: 600, marginBottom: '3px' }}>{label}</div>
                <textarea rows={2} value={memEdits[key]}
                  onChange={(e) => setMemEdits((p) => ({ ...p, [key]: e.target.value }))}
                  style={{ ...fld, resize: 'vertical' }} />
                <button onClick={() => onSaveMemoryField(key, memEdits[key])} disabled={saving} style={btn(saving)}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            ))}
          </div>
        )}
        {tab === 'voices' && (
          <div>
            <div style={{ marginBottom: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => onSetAutoGroupEnabled && onSetAutoGroupEnabled((v) => !v)}
                style={{ ...btn(false), marginTop: 0 }}
                title="Enable or disable multi-voice chained turns"
              >
                Multi-voice lines: {autoGroupEnabled ? 'ON' : 'OFF'}
              </button>
              <select
                value={autoGroupMaxTurns || 3}
                onChange={(e) => onSetAutoGroupMaxTurns && onSetAutoGroupMaxTurns(Math.min(6, Math.max(1, Number(e.target.value) || 3)))}
                style={{ ...fld, width: '140px', fontSize: '11px' }}
                title="How many lines in the auto exchange"
              >
                <option value={1}>1 line</option>
                <option value={2}>2 lines</option>
                <option value={3}>3 lines</option>
                <option value={4}>4 lines</option>
                <option value={5}>5 lines</option>
                <option value={6}>6 lines</option>
              </select>
            </div>

            {/* Voice speed slider */}
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#c47d2e', fontWeight: 600 }}>Voice Speed</span>
                <span style={{ color: '#d9c2a0', fontVariantNumeric: 'tabular-nums' }}>{Number(speechRate).toFixed(2)}×</span>
              </div>
              <input
                type="range" min="0.5" max="2" step="0.05"
                value={speechRate}
                onChange={(e) => onSpeechRateChange && onSpeechRateChange(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#c47d2e' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#5a4030', marginTop: '2px' }}>
                <span>0.5×</span><span>1×</span><span>2×</span>
              </div>
            </div>
            <div style={{ color: '#5a4838', marginBottom: '10px', lineHeight: 1.6 }}>
              Pick either an ElevenLabs or OpenAI voice per character. Saved to Firebase — no redeploy needed.
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setVoiceEdits(openAiDefaults)}
                style={{ ...btn(false), marginTop: 0, color: '#8fd9ff', borderColor: '#2f516d' }}
                title="Load OpenAI defaults into the selector"
              >
                Load OpenAI defaults
              </button>
            </div>
            {['SELENE', 'ADAM', 'RACHEL', 'SARA'].map((key) => (
              <div key={key} style={{ marginBottom: '12px' }}>
                <div style={{ color: VCOL[key], fontWeight: 600, marginBottom: '3px' }}>
                  {key.charAt(0) + key.slice(1).toLowerCase()}
                  <span style={{ color: '#4a3828', fontWeight: 400, marginLeft: '8px', fontFamily: 'monospace', fontSize: '11px' }}>
                    {voiceMap?.[key]?.id || '—'}
                  </span>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#8a7458', fontSize: '11px', marginBottom: '6px' }}>
                  <input
                    type="checkbox"
                    checked={!!activeVoices?.[key]}
                    onChange={() => {
                      if (!onSetActiveVoices) return;
                      onSetActiveVoices((prev) => {
                        const next = { ...prev, [key]: !prev?.[key] };
                        if (!next.SELENE && !next.ADAM && !next.RACHEL && !next.SARA) next[key] = true;
                        return next;
                      });
                    }}
                  />
                  Active in chat
                </label>
                <select
                  value={voiceEdits[key]}
                  onChange={(e) => setVoiceEdits((p) => ({ ...p, [key]: e.target.value }))}
                  style={{ ...fld, fontFamily: 'monospace', resize: 'none' }}
                >
                  <optgroup label="OpenAI voices">
                    {(Array.isArray(voiceCatalog?.openai) ? voiceCatalog.openai : []).map((opt) => (
                      <option key={`voice-${key}-oa-${opt.id}`} value={`openai:${opt.id}`}>
                        {opt.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="ElevenLabs voices">
                    {(CHARACTER_VOICES[key] || []).length > 0
                      ? (CHARACTER_VOICES[key] || []).map((opt) => (
                          <option key={`voice-${key}-el-${opt.id}`} value={`elevenlabs:${opt.id}`}>
                            {opt.label}
                          </option>
                        ))
                      : <option disabled value="">— no voices configured —</option>
                    }
                  </optgroup>
                </select>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
              <button onClick={() => onSaveVoices(voiceEdits)} disabled={saving}
                style={{ ...btn(saving), padding: '3px 14px' }}>
                {saving ? 'Saving…' : 'Save all voices'}
              </button>
              {['SELENE', 'ADAM', 'RACHEL', 'SARA'].map((key) => (
                <button key={key} onClick={() => onTestTts((String(voiceEdits[key] || '').split(':')[1]) || voiceMap?.[key]?.id || '')}
                  style={{ ...btn(false), color: VCOL[key], borderColor: VCOL[key] + '55', padding: '3px 10px' }}
                  title={`Test current ${key} voice: ${(String(voiceEdits[key] || '').split(':')[1]) || voiceMap?.[key]?.id || '(default)'}`}>
                  Test {key.charAt(0) + key.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>
        )}
        {tab === 'avatar' && (
          <div style={{ lineHeight: 1.6 }}>
            <div style={{ color: '#d9a15d', fontWeight: 600, marginBottom: '6px' }}>Selene Avatar Videos</div>
            <div style={{ color: '#8a7458', marginBottom: '8px' }}>
              Drop <strong style={{ color: '#bda07a' }}>video files</strong> onto the avatar strip above.<br />
              Drop 1 file → idle &nbsp;·&nbsp; Drop 2 files → idle + talking
            </div>
            <div style={{ color: '#3a2e20', fontSize: '11px' }}>
              Permanent: copy to <code style={{ color: '#6a5838' }}>public/selene-idle.mp4</code> &amp; <code style={{ color: '#6a5838' }}>selene-talking.mp4</code>, rebuild + redeploy.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SeleneAvatarStrip({ ttsPlaying }) {
  const idleRef = useRef(null);
  const talkingRef = useRef(null);
  const idleInputRef = useRef(null);
  const talkingInputRef = useRef(null);
  const [hasIdleVideo, setHasIdleVideo] = useState(false);
  const [hasTalkingVideo, setHasTalkingVideo] = useState(false);
  const [localIdleUrl, setLocalIdleUrl] = useState('');
  const [localTalkingUrl, setLocalTalkingUrl] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);

  const idleSrc = localIdleUrl || SELENE_IDLE_VIDEO;
  const talkingSrc = localTalkingUrl || (hasTalkingVideo ? SELENE_TALKING_VIDEO : idleSrc);

  useEffect(() => {
    let active = true;
    async function probe(url) {
      try {
        const response = await fetch(url, { method: 'HEAD' });
        return response.ok;
      } catch {
        return false;
      }
    }
    Promise.all([probe(SELENE_IDLE_VIDEO), probe(SELENE_TALKING_VIDEO)]).then(([idleOk, talkingOk]) => {
      if (!active) return;
      setHasIdleVideo(idleOk);
      setHasTalkingVideo(talkingOk);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const idleEl = idleRef.current;
    const talkingEl = talkingRef.current;
    if (!idleEl || !talkingEl) return;

    if (ttsPlaying && hasTalkingVideo) {
      idleEl.pause();
      talkingEl.currentTime = 0;
      talkingEl.play().catch(() => {});
    } else {
      talkingEl.pause();
      idleEl.play().catch(() => {});
    }
  }, [ttsPlaying, hasTalkingVideo, idleSrc, talkingSrc]);

  useEffect(() => () => {
    if (localIdleUrl) URL.revokeObjectURL(localIdleUrl);
    if (localTalkingUrl) URL.revokeObjectURL(localTalkingUrl);
  }, [localIdleUrl, localTalkingUrl]);

  function applyPickedFile(file, kind) {
    if (!file || !file.type.startsWith('video/')) return;
    const nextUrl = URL.createObjectURL(file);
    if (kind === 'idle') {
      if (localIdleUrl) URL.revokeObjectURL(localIdleUrl);
      setLocalIdleUrl(nextUrl);
      setHasIdleVideo(true);
      return;
    }
    if (localTalkingUrl) URL.revokeObjectURL(localTalkingUrl);
    setLocalTalkingUrl(nextUrl);
    setHasTalkingVideo(true);
  }

  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation(); // Don't bubble to the Back Room panel drop zone
    setIsDragActive(false);
    const files = Array.from(event.dataTransfer?.files || []).filter((file) => file.type.startsWith('video/'));
    if (files.length === 0) return;
    if (files.length === 1) {
      applyPickedFile(files[0], hasIdleVideo || localIdleUrl ? 'talking' : 'idle');
      return;
    }
    applyPickedFile(files[0], 'idle');
    applyPickedFile(files[1], 'talking');
  }

  const videoReady = hasIdleVideo || hasTalkingVideo;

  return (
    <div
      onDragOver={(event) => { event.preventDefault(); setIsDragActive(true); }}
      onDragLeave={() => setIsDragActive(false)}
      onDrop={handleDrop}
      style={{
        height: '150px',
        width: '100%',
        borderRadius: '10px',
        border: isDragActive ? '1px solid #c47d2e' : '1px solid #21170b',
        overflow: 'hidden',
        background: 'radial-gradient(120% 90% at 50% 20%, #1b140c 0%, #0f0b07 62%, #0b0907 100%)',
        position: 'relative',
      }}
    >
      {videoReady ? (
        <>
          <video
            ref={idleRef}
            src={idleSrc}
            poster={SELENE_POSTER}
            muted
            playsInline
            loop
            autoPlay
            preload="metadata"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: ttsPlaying && hasTalkingVideo ? 0 : 1,
              transition: 'opacity 160ms linear',
            }}
          />
          <video
            ref={talkingRef}
            src={talkingSrc}
            poster={SELENE_POSTER}
            muted
            playsInline
            loop
            autoPlay={false}
            preload="metadata"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: ttsPlaying && hasTalkingVideo ? 1 : 0,
              transition: 'opacity 120ms linear',
            }}
          />
        </>
      ) : (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          color: '#8b6f4e',
          fontSize: '12px',
          textAlign: 'center',
          padding: '0 14px',
          lineHeight: 1.4,
        }}>
          <div style={{ marginBottom: '10px' }}>
            Drop local videos here or pick them below.<br />
            Production files still live at <strong>/selene-idle.mp4</strong> and <strong>/selene-talking.mp4</strong>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={() => idleInputRef.current?.click()}
              style={{
                background: '#2a1808',
                border: '1px solid #5a3a18',
                color: '#d9a15d',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Choose Idle Video
            </button>
            <button
              onClick={() => talkingInputRef.current?.click()}
              style={{
                background: '#2a1808',
                border: '1px solid #5a3a18',
                color: '#d9a15d',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Choose Talking Video
            </button>
          </div>
          <input
            ref={idleInputRef}
            type="file"
            accept="video/*"
            style={{ display: 'none' }}
            onChange={(event) => applyPickedFile(event.target.files?.[0], 'idle')}
          />
          <input
            ref={talkingInputRef}
            type="file"
            accept="video/*"
            style={{ display: 'none' }}
            onChange={(event) => applyPickedFile(event.target.files?.[0], 'talking')}
          />
        </div>
      )}

      <div style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(255, 214, 158, 0.05) 0%, rgba(0, 0, 0, 0.2) 70%)',
      }} />
    </div>
  );
}

function LoadingDots() {
  return (
    <div style={{ display: 'flex', gap: '5px', alignItems: 'center', padding: '6px 0' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: '#6b4a28',
            animation: 'brPulse 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes brPulse {
          0%, 60%, 100% { opacity: 0.25; transform: scale(0.8); }
          30% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
